/**
 * The drag-and-drop id vocabulary, shared by the components that declare the
 * drop targets and the hook that translates a drop back into an action.
 *
 * Keeping the ids in one place is what lets the preview express placements the
 * library has no native concept of: a *row* is its own horizontal drop target
 * (so a drop at index 0 means "pair to the left"), a *gap* between rows is
 * another (so a drop there means "insert a new row"), and each photo anchor is
 * its own (so an overlay lands where the user aimed) — spec R50–R52.
 */

import { FieldId, PhotoAnchor, RegionId } from '../types';
import { isKnownFieldId } from './fields.config';

/** Drops are only allowed between droppables of the same type. */
export const CARD_BLOCK_DND_TYPE = 'card-block';
export const RAIL_DND_TYPE = 'rail-field';

export const RAIL_DROPPABLE_ID = 'rail';
export const FLOATING_DROPPABLE_ID = 'floating';

export const INFO_GAP_PREFIX = 'info-gap-';
export const INFO_ROW_PREFIX = 'info-row-';
export const PHOTO_PREFIX = 'photo-';

export const photoAnchorDroppableId = (anchor: PhotoAnchor): string =>
  `${PHOTO_PREFIX}${anchor}`;

/** Insert-a-new-row lane above row `index` (`rows.length` = below the last). */
export const infoGapDroppableId = (index: number): string => `${INFO_GAP_PREFIX}${index}`;

/** Join-this-row lane, which is what turns a drop into a side-by-side pair. */
export const infoRowDroppableId = (index: number): string => `${INFO_ROW_PREFIX}${index}`;

export const blockDraggableId = (fieldId: FieldId): string => `block-${fieldId}`;
export const railDraggableId = (fieldId: FieldId): string => `rail-${fieldId}`;

/** `block-listPrice` / `rail-listPrice` → `listPrice`. */
export const parseDraggableFieldId = (draggableId: string): FieldId | null => {
  const raw = draggableId.replace(/^(?:rail|block)-/, '');
  return isKnownFieldId(raw) ? raw : null;
};

export const regionForDroppable = (droppableId: string): RegionId | null => {
  if (droppableId.startsWith(PHOTO_PREFIX)) {
    return 'photo';
  }
  if (droppableId.startsWith(INFO_GAP_PREFIX) || droppableId.startsWith(INFO_ROW_PREFIX)) {
    return 'info';
  }
  return droppableId === FLOATING_DROPPABLE_ID ? 'floating' : null;
};

/** The numeric suffix of a lane id, defaulting to 0 for an unparseable id. */
export const parseLaneIndex = (droppableId: string, prefix: string): number => {
  const parsed = Number(droppableId.slice(prefix.length));
  return Number.isFinite(parsed) ? parsed : 0;
};
